import React, { useState } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion } from "framer-motion";
import { ArrowRight, Puzzle, Brain, Grid3X3, Gamepad2, Trophy, Footprints, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { Game2048 } from "@/components/games/Game2048";
import { SlidingPuzzle } from "@/components/games/SlidingPuzzle";
import { SimonSays } from "@/components/games/SimonSays";
import DinoRunner from "@/components/games/DinoRunner";
import VerticalPong from "@/components/games/VerticalPong";

import BalloonPopper from "@/components/games/BalloonPopper";


// Simple Memory Game Implementation
function MemoryGame({ t }: { t: (key: string) => string }) {
  const emojis = ["🍎", "🍊", "🍋", "🍇", "🍓", "🍉", "🍑", "🥝"];
  const [cards, setCards] = useState(() => {
    const shuffled = [...emojis, ...emojis]
      .sort(() => Math.random() - 0.5)
      .map((emoji, index) => ({
        id: index,
        emoji,
        isFlipped: false,
        isMatched: false,
      }));
    return shuffled;
  });
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [moves, setMoves] = useState(0);

  const handleCardClick = (id: number) => {
    if (flippedCards.length === 2) return;
    if (cards[id].isFlipped || cards[id].isMatched) return;

    const newCards = [...cards];
    newCards[id].isFlipped = true;
    setCards(newCards);

    const newFlipped = [...flippedCards, id];
    setFlippedCards(newFlipped);

    if (newFlipped.length === 2) {
      setMoves((m) => m + 1);
      const [first, second] = newFlipped;
      if (cards[first].emoji === cards[second].emoji) {
        setTimeout(() => {
          const matched = [...cards];
          matched[first].isMatched = true;
          matched[second].isMatched = true;
          setCards(matched);
          setFlippedCards([]);
        }, 500);
      } else {
        setTimeout(() => {
          const reset = [...cards];
          reset[first].isFlipped = false;
          reset[second].isFlipped = false;
          setCards(reset);
          setFlippedCards([]);
        }, 1000);
      }
    }
  };

  const resetGame = () => {
    const shuffled = [...emojis, ...emojis]
      .sort(() => Math.random() - 0.5)
      .map((emoji, index) => ({
        id: index,
        emoji,
        isFlipped: false,
        isMatched: false,
      }));
    setCards(shuffled);
    setFlippedCards([]);
    setMoves(0);
  };

  const isWon = cards.every((card) => card.isMatched);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-lg text-muted-foreground">{t("moves")}: {moves}</span>
        </div>
        <Button
          onClick={resetGame}
          variant="outline"
          data-scan-item
        >
          {t("newGame")}
        </Button>
      </div>

      {isWon && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="flex items-center justify-center gap-3 p-4 bg-success/20 rounded-xl border border-success"
        >
          <Trophy className="w-6 h-6 text-success" />
          <span className="text-xl font-bold text-success">{t("wonInMoves").replace("{0}", String(moves))}</span>
        </motion.div>
      )}

      <div className="grid grid-cols-4 gap-3 max-w-md mx-auto">
        {cards.map((card, index) => (
          <motion.button
            key={card.id}
            onClick={() => handleCardClick(card.id)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-scan-item
            className={cn(
              "aspect-square rounded-xl text-4xl flex items-center justify-center",
              "transition-all duration-300 border-2",
              card.isFlipped || card.isMatched
                ? "bg-primary/10 border-primary"
                : "bg-card border-border hover:border-primary",
              card.isMatched && "bg-success/20 border-success"
            )}
          >
            {card.isFlipped || card.isMatched ? card.emoji : "?"}
          </motion.button>
        ))}
      </div>
    </div>
  );
}

export default function GamesPage() {
  const navigate = useNavigate();
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const { t, dir } = useTranslation();
  
  // All games organized by category
  const games = [
    // Classic games (work with all control methods)
    {
      id: "memory",
      title: t("memoryGame"),
      description: t("memoryGameDesc"),
      icon: Brain,
      color: "from-primary/20 to-accent/20",
    },
    {
      id: "2048",
      title: t("game2048"),
      description: t("game2048Desc"),
      icon: Grid3X3,
      color: "from-accent/20 to-primary/20",
    },
    {
      id: "puzzle",
      title: t("slidingPuzzle"),
      description: t("slidingPuzzleDesc"),
      icon: Puzzle,
      color: "from-muted to-primary/10",
    },
    {
      id: "simon",
      title: t("simonSays"),
      description: t("simonSaysDesc"),
      icon: Gamepad2,
      color: "from-primary/20 to-muted",
    },
    // Single switch game
    {
      id: "dino",
      title: t("dinoRunner") || "דינו ראנר",
      description: t("dinoRunnerDesc") || "קפוץ מעל מכשולים - מתג אחד",
      icon: Footprints,
      color: "from-yellow-500/20 to-orange-500/20",
    },
    // Two switches game
    {
      id: "pong",
      title: t("pongGame") || "פונג",
      description: t("pongGameDesc") || "טניס שולחן - שני מתגים",
      icon: Gamepad2,
      color: "from-cyan-500/20 to-blue-500/20",
    },
    // Eye gaze game
    {
      id: "balloon",
      title: t("balloonPopper") || "פוצץ בלונים",
      description: t("balloonPopperDesc") || "מיקוד מבט - ללא לחיצות",
      icon: Eye,
      color: "from-sky-500/20 to-cyan-500/20",
    },
  ];


  // Fullscreen games (return directly without MainLayout)
  if (selectedGame === "dino") {
    return <DinoRunner onBack={() => setSelectedGame(null)} />;
  }
  if (selectedGame === "pong") {
    return <VerticalPong onBack={() => setSelectedGame(null)} />;
  }
  if (selectedGame === "balloon") {
    return <BalloonPopper onBack={() => setSelectedGame(null)} />;
  }

  const renderGame = () => {
    switch (selectedGame) {
      case "memory":
        return <MemoryGame t={t} />;
      case "2048":
        return <Game2048 t={t} />;
      case "puzzle":
        return <SlidingPuzzle t={t} />;
      case "simon":
        return <SimonSays t={t} />;
      default:
        return null;
    }
  };

  return (
    <MainLayout>
      <div className="flex-1 flex flex-col overflow-hidden" dir={dir}>
        <header className="flex items-center gap-3 px-6 py-4 border-b border-border bg-card/50">
          <h1 className="text-2xl font-bold">
            {selectedGame ? games.find((g) => g.id === selectedGame)?.title : t("gamesTitle")}
          </h1>
        </header>

        <div className="flex-1 overflow-auto p-6">
          {!selectedGame ? (
            <div className="space-y-6">
              {/* Games Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 rounded-2xl transition-all">
                {games.map((game, index) => {
                  const Icon = game.icon;
                  return (
                    <motion.button
                      key={game.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => setSelectedGame(game.id)}
                      data-scan-item
                      className={cn(
                        "flex flex-col items-center justify-center gap-3 p-6 rounded-2xl",
                        "bg-gradient-to-br border-2 border-border",
                        "hover:scale-105 hover:shadow-lg hover:border-primary",
                        "active:scale-95 transition-all duration-200",
                        game.color
                      )}
                    >
                      <Icon className="w-10 h-10 text-primary" />
                      <span className="text-lg font-bold">{game.title}</span>
                      <span className="text-sm text-muted-foreground text-center">
                        {game.description}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto">
              {renderGame()}
            </div>
          )}
        </div>
        
      </div>
    </MainLayout>
  );
}
